/**
 * Company Settings Configuration Page
 *
 * Allows administrators to configure:
 * - Company branding (name, tagline, logo)
 * - Email domain configuration
 * - Support contact information
 * - Multiple domain support
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  Spin,
  Typography,
  Upload,
  Alert,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import Axios from "axios";
import StatCard from '@/components/StatCard';
import { Building2, Mail, Globe, CheckCircle } from "lucide-react";
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import PageHeader from '@/components/PageHeader';
import { PageInfo } from '@/components/PageChrome';
import { useTranslation } from 'react-i18next';
import { useConfirmWithReason } from '@/components/ConfirmWithReason';
import useModuleAccess from '@/hooks/useModuleAccess';

const { Text } = Typography;
const { TextArea } = Input;

const CompanySettings = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const acl = useModuleAccess('Company Settings');
  const { confirm: confirmWithReason, modal: confirmModal } = useConfirmWithReason();

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : 'rgba(0, 0, 0, 0.45)';
  const panelBg = isDark ? defaultColors.palette.gray[700] : '#f9fafb';
  const panelBorder = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const panelStyle = { background: panelBg, border: `1px solid ${panelBorder}`, borderLeft: `4px solid ${defaultColors.primary}`, borderRadius: 12, padding: '16px 24px 4px', marginBottom: 16 };
  const panelTitle = (icon, text) => (
    <div className="flex items-center gap-2 mb-4">
      <div className="p-1.5 rounded-md" style={{ backgroundColor: isDark ? defaultColors.palette.teal[900] : defaultColors.palette.teal[50], color: defaultColors.primary, display: 'inline-flex' }}>{icon}</div>
      <span className="text-sm font-semibold" style={{ color: defaultColors.primary }}>{text}</span>
    </div>
  );

  // Fetch company settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("Token");
      const response = await Axios.get("/bp/company-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        const data = response.data.data;
        setSettings(data);
        form.setFieldsValue({
          company_name: data.company_name,
          company_short_name: data.company_short_name,
          company_tagline: data.company_tagline,
          primary_email_domain: data.primary_email_domain,
          allowed_email_domains: data.allowed_email_domains,
          support_email: data.support_email,
          info_email: data.info_email,
          company_website: data.company_website,
          company_address: data.company_address,
          company_phone: data.company_phone,
        });
      } else {
        toast.error("Failed to fetch company settings");
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Error fetching company settings");
    } finally {
      setLoading(false);
    }
  };

  // Company settings appear on every printed batch record: audited change, reason required (F28).
  const handleSave = async (values) => {
    const r = await confirmWithReason({
      title: 'Save company settings',
      confirmText: 'Save settings',
      description: 'Branding, email domains and contact details are used on printed batch records and notifications.',
    });
    if (!r) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("Token");
      const response = await Axios.post(
        "/bp/company-settings/update",
        { ...values, reason: r.reason },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        toast.success("Company settings updated successfully");
        fetchSettings(); // Refresh data
      } else {
        toast.error(response.data.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Error saving company settings");
    } finally {
      setSaving(false);
    }
  };

  // Backend route is /bp/company-settings/upload-logo (F29 — the old /api/ prefix returned 404).
  const handleLogoUpload = async (file) => {
    const r = await confirmWithReason({
      title: 'Replace company logo',
      confirmText: 'Upload logo',
      description: `"${file?.name}" will replace the logo printed on batch records.`,
    });
    if (!r) return false;
    const formData = new FormData();
    formData.append("logo", file);
    formData.append("reason", r.reason);

    try {
      const token = localStorage.getItem("Token");
      const response = await Axios.post("/bp/company-settings/upload-logo", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data || {};

      if (data.status === "success") {
        toast.success("Company logo uploaded successfully");
        fetchSettings(); // Refresh to show new logo
      } else {
        toast.error(data.message || "Failed to upload logo");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Error uploading company logo");
    }

    return false; // Prevent default upload behavior
  };

  const handleReset = () => {
    if (settings) {
      form.setFieldsValue({
        company_name: settings.company_name,
        company_short_name: settings.company_short_name,
        company_tagline: settings.company_tagline,
        primary_email_domain: settings.primary_email_domain,
        allowed_email_domains: settings.allowed_email_domains,
        support_email: settings.support_email,
        info_email: settings.info_email,
        company_website: settings.company_website,
        company_address: settings.company_address,
        company_phone: settings.company_phone,
      });
      message.info("Form reset to saved values");
    }
  };

  if (loading && !settings) {
    return (
      <Card style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" tip="Loading company settings..." />
      </Card>
    );
  }

  return (
    <div className="p-2.5 pt-5" style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}>
    <Card
      className="border border-gray-300 shadow-md"
      bordered={false}
      classNames={{ body: 'page-card-scroll' }}
      style={{ backgroundColor: cardBgColor, height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
      styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
      title={
        <PageHeader
          compact
          icon={<Icons.Building2 size={22} />}
          title={t('Company Settings')}
          description="Company branding, email domains and contact information."
          meta={<PageInfo text="Primary email domain is used for auto-generating user email addresses. Allowed email domains supports multiple domains for organizations with subsidiaries. Changes take effect immediately for new users and email generation. Existing user email addresses are not automatically updated." />}
          secondary={[{ key: 'reset', label: 'Reset', icon: <ReloadOutlined />, onClick: handleReset }]}
          primary={{ label: 'Save Settings', icon: <SaveOutlined />, onClick: () => form.submit(), loading: saving, disabled: !acl.update }}
        />
      }
    >
      {confirmModal}
      {!acl.update && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Read-only"
          description="Your role can view company settings but not change them."
        />
      )}

      {/* Top Summary Cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <StatCard
            label="Company Profile"
            value={settings?.company_name ? "Configured" : "Pending"}
            icon={<Building2 size={24} />}
            bgColor={`${defaultColors.primary}22`}
            iconColor={defaultColors.primary}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            label="Email Domains"
            value={(settings?.allowed_email_domains?.split(',')?.length || 0) + 1}
            icon={<Mail size={24} />}
            bgColor={`${defaultColors.info}22`}
            iconColor={defaultColors.info}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            label="Website Status"
            value={settings?.company_website ? "Active" : "Not Set"}
            icon={<Globe size={24} />}
            bgColor={`${defaultColors.warning}22`}
            iconColor={defaultColors.warning}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            label="Configuration Status"
            value={settings?.company_name && settings?.primary_email_domain ? "Complete" : "Incomplete"}
            icon={<CheckCircle size={24} />}
            bgColor={`${defaultColors.success}22`}
            iconColor={defaultColors.success}
          />
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          company_name: "Quantum Pharmaceuticals",
          company_short_name: "QuantumRx",
          primary_email_domain: "quantumrx.com",
        }}
      >
        {/* Company Branding Section */}
        <div style={panelStyle}>
          {panelTitle(<GlobalOutlined />, "Company Branding")}

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Company Name (Legal)"
              name="company_name"
              rules={[
                { required: true, message: "Please enter company name" },
              ]}
              tooltip="Full legal company name"
            >
              <Input placeholder="e.g., Quantum Pharmaceuticals" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Short Name (Brand)"
              name="company_short_name"
              rules={[
                { required: true, message: "Please enter short name" },
              ]}
              tooltip="Brand name used in UI"
            >
              <Input placeholder="e.g., QuantumRx" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Company Tagline"
              name="company_tagline"
              tooltip="Company tagline or motto"
            >
              <Input placeholder="e.g., Precision Medicine, Quantum Results" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Company Logo"
              tooltip="Upload company logo (PNG, JPG, SVG)"
            >
              <Upload
                beforeUpload={handleLogoUpload}
                accept=".png,.jpg,.jpeg,.svg,.gif"
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />} disabled={!acl.update}>Upload Logo</Button>
              </Upload>
              {settings?.company_logo && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={settings.company_logo}
                    alt="Company Logo"
                    style={{ maxHeight: 60, marginTop: 8 }}
                  />
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* Email Domain Configuration Section */}
        </div>

        <div style={panelStyle}>
          {panelTitle(<MailOutlined />, "Email Domain Configuration")}

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Primary Email Domain"
              name="primary_email_domain"
              rules={[
                { required: true, message: "Please enter primary email domain" },
                {
                  pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/,
                  message: "Please enter a valid domain (without @)",
                },
              ]}
              tooltip="Primary email domain without @ symbol"
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <InfoCircleOutlined /> Used for auto-generating user email addresses
                </Text>
              }
            >
              <Input
                prefix="@"
                placeholder="quantumrx.com"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Allowed Email Domains"
              name="allowed_email_domains"
              tooltip="Comma-separated list of allowed domains for multi-domain organizations"
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <InfoCircleOutlined /> For subsidiaries: domain1.com, domain2.com, domain3.com
                </Text>
              }
            >
              <Input placeholder="subsidiary1.com, subsidiary2.com" />
            </Form.Item>
          </Col>
        </Row>

        {/* Contact Information Section */}
        </div>

        <div style={panelStyle}>
          {panelTitle(<PhoneOutlined />, "Contact Information")}

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Support Email"
              name="support_email"
              rules={[
                {
                  type: "email",
                  message: "Please enter a valid email",
                },
              ]}
              tooltip="Customer support email address"
            >
              <Input placeholder="support@quantumrx.com" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Info Email"
              name="info_email"
              rules={[
                {
                  type: "email",
                  message: "Please enter a valid email",
                },
              ]}
              tooltip="General information email address"
            >
              <Input placeholder="info@quantumrx.com" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Company Website"
              name="company_website"
              rules={[
                {
                  type: "url",
                  message: "Please enter a valid URL",
                },
              ]}
            >
              <Input placeholder="https://www.quantumrx.com" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Company Phone" name="company_phone">
              <Input placeholder="+1 (555) 123-4567" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item label="Company Address" name="company_address">
              <TextArea
                rows={3}
                placeholder="123 Innovation Drive, Precision Valley, CA 94000, USA"
              />
            </Form.Item>
          </Col>
        </Row>

        </div>

        {/* Info Section */}
        {settings && (
          <div style={{ marginTop: 8, padding: '8px 4px' }}>
            <Text style={{ fontSize: 12, color: secondaryTextColor }}>
              Last updated: {settings.updated_at || "Never"}
              {settings.updated_by && ` by ${settings.updated_by}`}
            </Text>
          </div>
        )}
      </Form>

    </Card>
    </div>
  );
};

export default CompanySettings;
